import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "@/App.css";
import axios from "axios";
import { Scale, CircleUserRound, CircleDot, Cross, Shield, Sparkles, Brain, Menu, X } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function App() {
  const location = useLocation();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    fullName: "",
    companyName: "",
    roleTitle: "",
    workEmail: "",
    monthlyVolume: "",
    capacityNeeded: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [cookieConsent, setCookieConsent] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    if (location.state?.scrollTo) {
      setTimeout(() => {
        document.getElementById(location.state.scrollTo)?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    } else {
      // Scroll to top when navigating to home without scrollTo state
      window.scrollTo(0, 0);
    }
  }, [location]);

  // Close mobile menu when route changes
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/contact`, formData);
      setSubmitSuccess(true);
      setFormData({
        fullName: "",
        companyName: "",
        roleTitle: "",
        workEmail: "",
        monthlyVolume: "",
        capacityNeeded: ""
      });
    } catch (error) {
      console.error("Error submitting form:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const scrollToSection = (id) => {
    setMobileMenuOpen(false);
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleMobileNavClick = (action) => {
    setMobileMenuOpen(false);
    if (typeof action === 'function') {
      action();
    }
  };

  return (
    <div className="app" data-testid="app-container">
      {/* Navigation */}
      <nav className="nav" data-testid="navigation">
        <div className="nav-container">
          <div className="nav-logo" data-testid="nav-logo">
            <span className="logo-text">Precision Prescribing</span>
          </div>
          
          {/* Mobile Menu Button */}
          <button 
            className="mobile-menu-btn" 
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-btn"
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
          
          {/* Desktop Navigation */}
          <div className="nav-links">
            <button onClick={() => navigate('/clinical-areas')} className="nav-link" data-testid="nav-clinical-areas">Clinical Areas</button>
            <button onClick={() => navigate('/dispensing')} className="nav-link" data-testid="nav-dispensing">Dispensing</button>
            <button onClick={() => scrollToSection('how-it-works')} className="nav-link" data-testid="nav-how-it-works">How It Works</button>
            <button onClick={() => scrollToSection('about')} className="nav-link" data-testid="nav-about">About</button>
            <button onClick={() => scrollToSection('contact')} className="nav-link nav-cta" data-testid="nav-contact-cta">Request a Discussion</button>
          </div>
          
          {/* Mobile Navigation */}
          <div className={`mobile-nav ${mobileMenuOpen ? 'mobile-nav-open' : ''}`} data-testid="mobile-nav">
            <button onClick={() => handleMobileNavClick(() => navigate('/clinical-areas'))} className="mobile-nav-link" data-testid="mobile-nav-clinical-areas">Clinical Areas</button>
            <button onClick={() => handleMobileNavClick(() => navigate('/dispensing'))} className="mobile-nav-link" data-testid="mobile-nav-dispensing">Dispensing</button>
            <button onClick={() => handleMobileNavClick(() => scrollToSection('how-it-works'))} className="mobile-nav-link" data-testid="mobile-nav-how-it-works">How It Works</button>
            <button onClick={() => handleMobileNavClick(() => scrollToSection('about'))} className="mobile-nav-link" data-testid="mobile-nav-about">About</button>
            <button onClick={() => handleMobileNavClick(() => scrollToSection('contact'))} className="mobile-nav-link mobile-nav-cta" data-testid="mobile-nav-contact-cta">Request a Discussion</button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="hero" data-testid="hero-section">
        <div className="hero-container">
          <div className="hero-content">
            <h1 className="hero-title" data-testid="hero-title">
              Pharmacist-led prescribing capacity, integrated into your clinical workflow
            </h1>
            <p className="hero-subtitle" data-testid="hero-subtitle">
              Delivered through asynchronous prescribing and video consultations, aligned with your SOPs and systems.
            </p>
            <div className="hero-buttons">
              <button onClick={() => scrollToSection('contact')} className="btn btn-primary" data-testid="hero-cta-discussion">
                Request a Confidential Discussion
              </button>
              <button onClick={() => scrollToSection('clinical-areas')} className="btn btn-secondary" data-testid="hero-cta-clinical">
                View Clinical Areas
              </button>
            </div>
            <p className="hero-note" data-testid="hero-note">
              B2B only. Prescribers work within your SOPs, systems and clinical pathways.
            </p>
          </div>
          <div className="hero-image">
            <img 
              src="https://customer-assets.emergentagent.com/job_prescriber-infra/artifacts/n7ey7qx7_ChatGPT%20Image%20Feb%2020%2C%202026%2C%2011_37_39%20PM.png" 
              alt="Remote prescribing consultation"
              data-testid="hero-image"
            />
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="about" data-testid="about-section">
        <div className="section-container">
          <h2 className="section-title" data-testid="about-title">About Precision Prescribing</h2>
          <p className="section-description" data-testid="about-b2b">
            Precision Prescribing provides pharmacist-led prescribing capacity and operational support to regulated healthcare providers, online clinics, pharmacies and healthcare organisations.
          </p>
          <p className="section-description" data-testid="about-description">
            We provide a pharmacist-led prescribing capacity layer for regulated digital healthcare providers. Our prescribers operate within your SOPs, systems, and clinical pathways, supporting scale without compromising governance. We focus on protocol-driven workflows, structured onboarding, and operational consistency across a range of clinical services.
          </p>
          <p className="section-note" data-testid="about-note">
            UK-based team with governance-first delivery and audit-ready documentation. Experienced in supporting regulated digital health services, with a strong understanding of requirements within GPhC-registered and CQC-aligned environments.
          </p>
          
          <div className="features-grid" data-testid="features-grid">
            <div className="feature-card" data-testid="feature-capacity">
              <h3>Flexible capacity</h3>
              <p>Scales with demand across services</p>
            </div>
            <div className="feature-card" data-testid="feature-governance">
              <h3>Governance-aligned</h3>
              <p>You retain clinical oversight</p>
            </div>
            <div className="feature-card" data-testid="feature-onboarding">
              <h3>Structured onboarding</h3>
              <p>Seamless integration into your workflow</p>
            </div>
          </div>
        </div>
      </section>

      {/* Clinical Areas Section */}
      <section id="clinical-areas" className="clinical-areas" data-testid="clinical-areas-section">
        <div className="section-container">
          <h2 className="section-title clinical-areas-heading" data-testid="clinical-areas-title">Clinical Areas We Support</h2>
          <p className="section-description" data-testid="clinical-areas-intro">
            Our prescribers support a range of protocol-driven clinical services.
          </p>
          
          <div className="clinical-grid" data-testid="clinical-grid">
            <div className="clinical-card" data-testid="clinical-weight">
              <Scale className="clinical-icon" size={26} strokeWidth={1.5} />
              <h3>Weight Management</h3>
            </div>
            <div className="clinical-card" data-testid="clinical-mens">
              <CircleUserRound className="clinical-icon" size={26} strokeWidth={1.5} />
              <h3>Men's Health</h3>
            </div>
            <div className="clinical-card" data-testid="clinical-womens">
              <CircleDot className="clinical-icon" size={26} strokeWidth={1.5} />
              <h3>Women's Health</h3>
            </div>
            <div className="clinical-card" data-testid="clinical-pain">
              <Cross className="clinical-icon" size={26} strokeWidth={1.5} />
              <h3>Pain Management</h3>
            </div>
            <div className="clinical-card" data-testid="clinical-infection">
              <Shield className="clinical-icon" size={26} strokeWidth={1.5} />
              <h3>Infection</h3>
            </div>
            <div className="clinical-card" data-testid="clinical-hair">
              <Sparkles className="clinical-icon" size={26} strokeWidth={1.5} />
              <h3>Hair Loss</h3>
            </div>
            <div className="clinical-card" data-testid="clinical-mental">
              <Brain className="clinical-icon" size={26} strokeWidth={1.5} />
              <h3>Mental Health</h3>
            </div>
          </div>
          
          <p className="clinical-note" data-testid="clinical-note">
            Additional clinical pathways can be supported based on your SOPs and governance framework.
          </p>
          
          <div className="clinical-cta" data-testid="clinical-cta">
            <p className="clinical-cta-text">Looking for a specific clinical pathway?</p>
            <button onClick={() => scrollToSection('contact')} className="btn btn-primary" data-testid="clinical-cta-btn">
              Discuss Your Requirements
            </button>
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="problem" data-testid="problem-section">
        <div className="section-container">
          <div className="problem-content">
            <div className="problem-text">
              <h2 className="section-title" data-testid="problem-title">When demand outgrows prescribing capacity</h2>
              <p className="section-description" data-testid="problem-description">
                Digital healthcare providers are experiencing sustained growth in prescribing demand. Recruitment delays, rising clinical costs, and inconsistent prescriber availability create operational strain and capacity risk.
              </p>
              <p className="problem-highlight" data-testid="problem-highlight">
                Scaling safely requires structured prescribing capacity — not ad hoc staffing.
              </p>
              <p className="section-note" data-testid="problem-note">
                Precision Prescribing provides pharmacist-led infrastructure designed for high-volume, protocol-driven digital prescribing environments.
              </p>
            </div>
            <div className="problem-image">
              <img 
                src="https://customer-assets.emergentagent.com/job_prescriber-infra/artifacts/b3mcf2fx_ChatGPT%20Image%20Feb%2020%2C%202026%2C%2011_38_16%20PM.png" 
                alt="Capacity management and prescriber allocation"
                data-testid="problem-image"
              />
            </div>
          </div>
          
          <div className="pain-points" data-testid="pain-points">
            <div className="pain-point" data-testid="pain-point-1">
              <h4>Longer review times and delayed turnarounds</h4>
              <p>Extended wait times impact patient satisfaction and retention</p>
            </div>
            <div className="pain-point" data-testid="pain-point-2">
              <h4>Peaks in demand causing backlogs</h4>
              <p>Volume surges overwhelm fixed capacity</p>
            </div>
            <div className="pain-point" data-testid="pain-point-3">
              <h4>Governance risk from inconsistent processes</h4>
              <p>Variable quality threatens regulatory compliance</p>
            </div>
          </div>
        </div>
      </section>

      {/* Solution Section */}
      <section className="solution" data-testid="solution-section">
        <div className="section-container">
          <h2 className="section-title" data-testid="solution-title">Structured, Pharmacy-Led Prescribing Capacity</h2>
          <p className="section-description" data-testid="solution-description">
            We supply experienced pharmacist independent prescribers to increase prescribing capacity while maintaining governance, consistency, and clinical quality.
          </p>
          
          <div className="solution-grid" data-testid="solution-grid">
            <div className="solution-card" data-testid="solution-card-1">
              <h3>Per-task capacity that scales with demand</h3>
              <p>Flexible resource allocation matched to your workflow requirements</p>
            </div>
            <div className="solution-card" data-testid="solution-card-2">
              <h3>No fixed employment overheads or long-term commitments</h3>
              <p>Reduce operational costs while maintaining service levels</p>
            </div>
            <div className="solution-card" data-testid="solution-card-3">
              <h3>Operational alignment to your SOPs and clinical pathway</h3>
              <p>Seamless integration with existing governance frameworks</p>
            </div>
            <div className="solution-card" data-testid="solution-card-4">
              <h3>Designed for protocol-driven digital healthcare services</h3>
              <p>Structured prescribing processes across multiple clinical pathways</p>
            </div>
            <div className="solution-card" data-testid="solution-card-5">
              <h3>Operational oversight and performance monitoring</h3>
              <p>Continuous quality assurance and coordination built in</p>
            </div>
            <div className="solution-card" data-testid="solution-card-6">
              <h3>Structured onboarding and training</h3>
              <p>Role-based training aligned to your SOPs, products and escalation pathways</p>
            </div>
          </div>
          
          <div className="solution-cta">
            <button onClick={() => scrollToSection('contact')} className="btn btn-primary" data-testid="solution-cta-btn">
              Request a Confidential Discussion
            </button>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="how-it-works" data-testid="how-it-works-section">
        <div className="section-container">
          <h2 className="section-title" data-testid="how-it-works-title">How It Works</h2>
          
          <div className="steps" data-testid="steps-container">
            <div className="step" data-testid="step-1">
              <span className="step-number">1</span>
              <h3>You define your clinical model</h3>
              <p>Set your SOPs, governance framework, prescribing pathways, and consultation approach.</p>
            </div>
            <div className="step" data-testid="step-2">
              <span className="step-number">2</span>
              <h3>We integrate with your workflow</h3>
              <p>Prescribers are aligned to your systems, pathways, and operational requirements.</p>
            </div>
            <div className="step" data-testid="step-3">
              <span className="step-number">3</span>
              <h3>You scale prescribing capacity</h3>
              <p>Receive flexible prescribing capacity that supports demand while maintaining governance, consistency, and clinical quality.</p>
            </div>
          </div>
          
          <p className="how-it-works-note" data-testid="how-it-works-note">
            We operate within your governance framework — providing prescribing capacity, not replacing clinical control.
          </p>
        </div>
      </section>

      {/* Standards Section */}
      <section id="standards" className="standards" data-testid="standards-section">
        <div className="section-container">
          <h2 className="section-title" data-testid="standards-title">Prescriber Standards</h2>
          <p className="section-description" data-testid="standards-description">
            Our network is built for reliable, protocol-driven prescribing in regulated environments.
          </p>
          
          <div className="standards-grid" data-testid="standards-grid">
            <div className="standard-card" data-testid="standard-eligibility">
              <h3>Eligibility</h3>
              <p className="standard-intro">UK-registered pharmacist prescribers, verified and ready to work within regulated pathways.</p>
              <ul>
                <li>UK pharmacist independent prescribers</li>
                <li>Professional indemnity in place</li>
                <li>Secure system access</li>
              </ul>
            </div>
            <div className="standard-card" data-testid="standard-clinical">
              <h3>Clinical discipline</h3>
              <p className="standard-intro">Protocol-driven prescribing with defined escalation routes and safety controls.</p>
              <ul>
                <li>Protocol-driven prescribing models across multiple clinical pathways</li>
                <li>Escalation routes for clinical queries and red flags</li>
                <li>Governance-first approach within client SOPs</li>
              </ul>
            </div>
            <div className="standard-card" data-testid="standard-operational">
              <h3>Operational controls</h3>
              <p className="standard-intro">Structured onboarding and ongoing quality review to maintain consistency at scale.</p>
              <ul>
                <li>Structured onboarding aligned to client SOPs and systems</li>
                <li>Ongoing performance monitoring and feedback</li>
                <li>Audit-ready operational documentation (where applicable)</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Governance Section */}
      <section className="governance" data-testid="governance-section">
        <div className="section-container">
          <div className="governance-content">
            <div className="governance-images">
              <img 
                src="https://customer-assets.emergentagent.com/job_prescriber-infra/artifacts/3x2b9z81_ChatGPT%20Image%20Feb%2020%2C%202026%2C%2011_38_11%20PM.png" 
                alt="Remote prescriber conducting clinical consultation"
                data-testid="governance-image-1"
              />
              <img 
                src="https://customer-assets.emergentagent.com/job_prescriber-infra/artifacts/2fxld27b_ChatGPT%20Image%20Feb%2020%2C%202026%2C%2011_38_20%20PM.png" 
                alt="Secure prescribing workflow and governance systems"
                data-testid="governance-image-2"
              />
            </div>
            <div className="governance-text">
              <h2 className="section-title" data-testid="governance-title">Governance-Aligned Infrastructure</h2>
              <p className="section-description" data-testid="governance-description">
                We integrate into existing governance frameworks without assuming clinical control. Prescribers operate within the engaging pharmacy's SOPs, systems and clinical pathway.
              </p>
              <p className="governance-note" data-testid="governance-note">
                Our processes are built to support audit readiness and inspection expectations.
              </p>
              <p className="governance-highlight" data-testid="governance-highlight">
                We provide operational coordination and performance monitoring while the engaging pharmacy retains clinical governance and oversight.
              </p>
              
              <div className="governance-features" data-testid="governance-features">
                <div className="governance-feature" data-testid="governance-feature-1">
                  <h4>Clear accountability boundaries</h4>
                  <p>Defined roles and responsibilities maintain regulatory compliance</p>
                </div>
                <div className="governance-feature" data-testid="governance-feature-2">
                  <h4>Audit-readiness support (operational)</h4>
                  <p>Documentation and reporting aligned with inspection requirements</p>
                </div>
                <div className="governance-feature" data-testid="governance-feature-3">
                  <h4>Standardised pathways and safety controls</h4>
                  <p>Consistent application of client SOPs and safeguarding procedures</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="contact" data-testid="contact-section">
        <div className="section-container">
          <h2 className="section-title" data-testid="contact-title">Discuss Your Prescribing Capacity Requirements</h2>
          <p className="section-description" data-testid="contact-description">
            Tell us about your service, expected demand, and clinical pathways. We'll outline how prescribing capacity can be integrated into your workflow.
          </p>
          
          {submitSuccess ? (
            <div className="success-message" data-testid="form-success-message">
              <h3>Thank you</h3>
              <p>Your enquiry has been received. We will respond within one business day.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="contact-form" data-testid="contact-form">
              <div className="form-group">
                <label htmlFor="fullName">Full Name *</label>
                <input
                  type="text"
                  id="fullName"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleInputChange}
                  required
                  data-testid="input-full-name"
                />
              </div>
              <div className="form-group">
                <label htmlFor="companyName">Company Name *</label>
                <input
                  type="text"
                  id="companyName"
                  name="companyName"
                  value={formData.companyName}
                  onChange={handleInputChange}
                  required
                  data-testid="input-company-name"
                />
              </div>
              <div className="form-group">
                <label htmlFor="roleTitle">Role / Title *</label>
                <input
                  type="text"
                  id="roleTitle"
                  name="roleTitle"
                  value={formData.roleTitle}
                  onChange={handleInputChange}
                  required
                  data-testid="input-role-title"
                />
              </div>
              <div className="form-group">
                <label htmlFor="workEmail">Work Email *</label>
                <input
                  type="email"
                  id="workEmail"
                  name="workEmail"
                  value={formData.workEmail}
                  onChange={handleInputChange}
                  required
                  data-testid="input-work-email"
                />
              </div>
              <div className="form-group">
                <label htmlFor="monthlyVolume">Estimated Monthly Prescribing Volume (optional)</label>
                <input
                  type="text"
                  id="monthlyVolume"
                  name="monthlyVolume"
                  value={formData.monthlyVolume}
                  onChange={handleInputChange}
                  data-testid="input-monthly-volume"
                />
              </div>
              <div className="form-group">
                <label htmlFor="capacityNeeded">What capacity do you need? *</label>
                <textarea
                  id="capacityNeeded"
                  name="capacityNeeded"
                  value={formData.capacityNeeded}
                  onChange={handleInputChange}
                  required
                  rows={4}
                  data-testid="input-capacity-needed"
                />
              </div>
              <button type="submit" className="btn btn-primary btn-full" disabled={isSubmitting} data-testid="form-submit-btn">
                {isSubmitting ? "Submitting..." : "Request a Confidential Discussion"}
              </button>
              <p className="trust-line" data-testid="trust-line">
                No obligation. We respond within one business day.
              </p>
              <p className="form-note" data-testid="form-privacy-note">
                By submitting, you agree to our <a href="/privacy-policy" data-testid="privacy-policy-link">Privacy Policy</a>. We will use your details to respond to your enquiry.
              </p>
            </form>
          )}
          
        </div>
      </section>

      {/* Footer */}
      <footer className="footer" data-testid="footer">
        <div className="footer-container">
          <div className="footer-brand" data-testid="footer-brand">
            <span className="logo-text">Precision Prescribing</span>
            <p className="footer-tagline">Pharmacist-led prescribing capacity for regulated healthcare providers.</p>
          </div>
          <div className="footer-info" data-testid="footer-info">
            <p className="footer-legal-line">Precision Prescribing is a trading name of PHARMKG LTD.</p>
            <p>Company number: 14702569</p>
            <p>ICO registration: ZC107443</p>
            <p>Registered office: 261 High Street, Waltham Cross, EN8 7BE</p>
            <p>Email: <a href="mailto:hello@precisionpx.com">hello@precisionpx.com</a></p>
            <nav className="footer-links" data-testid="footer-links" aria-label="Legal">
              <a href="/privacy-policy" className="footer-link" data-testid="footer-privacy-link">Privacy Policy</a>
              <span className="footer-sep" aria-hidden="true">|</span>
              <a href="/cookie-policy" className="footer-link" data-testid="footer-cookie-link">Cookie Policy</a>
              <span className="footer-sep" aria-hidden="true">|</span>
              <a href="/terms-of-use" className="footer-link" data-testid="footer-terms-link">Website Terms of Use</a>
              <span className="footer-sep" aria-hidden="true">|</span>
              <button
                onClick={() => scrollToSection('contact')}
                className="footer-link"
                data-testid="footer-contact-link"
              >
                Contact
              </button>
            </nav>
          </div>
        </div>
      </footer>

      {/* Cookie Banner */}
      {cookieConsent === null && (
        <div className="cookie-banner" data-testid="cookie-banner">
          <p>
            We use cookies and analytics to improve your experience and understand how our site is used. By clicking "Accept", you consent to our use of cookies and Google Analytics in accordance with UK GDPR. <a href="/privacy-policy" data-testid="cookie-privacy-link">Privacy Policy</a>
          </p>
          <div className="cookie-buttons">
            <button onClick={() => setCookieConsent(false)} className="btn btn-secondary" data-testid="cookie-decline-btn">
              Decline
            </button>
            <button onClick={() => setCookieConsent(true)} className="btn btn-primary" data-testid="cookie-accept-btn">
              Accept Cookies
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
